import { useState } from "react";
import { toast } from "sonner";
import {
  authHeaders,
  handleUnauthorized,
} from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function badgeClass(status) {
  switch (status) {
    case "paid":
      return "bg-emerald-500/15 border-emerald-500/30 text-emerald-300";
    case "pending":
      return "bg-amber-500/15 border-amber-500/30 text-amber-300";
    case "refunded":
      return "bg-zinc-500/15 border-zinc-500/30 text-zinc-300";
    case "active":
      return "bg-emerald-500/15 border-emerald-500/30 text-emerald-300";
    case "suspended":
      return "bg-red-500/15 border-red-500/30 text-red-300";
    case "pending_payment":
      return "bg-amber-500/15 border-amber-500/30 text-amber-300";
    default:
      return "bg-white/5 border-white/10 text-zinc-300";
  }
}

/**
 * Inline payment and account summary shown on a Restaurant Page row after it
 * has been converted to a sales lead. Lets the owner save a payment link,
 * copy the live TapToTable Page URL, and mark the restaurant paid/active.
 */
export default function MockupConversionPanel({ conversion, onUpdate }) {
  const [link, setLink] = useState(conversion.payment_link || "");
  const [savingLink, setSavingLink] = useState(false);
  const [marking, setMarking] = useState(false);

  const patch = async (body) => {
    const res = await fetch(`${API}/leads/${conversion.lead_id}/payment`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
      body: JSON.stringify(body),
    });

    if (res.status === 401 || res.status === 403) {
      handleUnauthorized();
      throw new Error("Unauthorized");
    }

    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.detail || `HTTP ${res.status}`);
    }

    return res.json();
  };

  const saveLink = async () => {
    setSavingLink(true);

    try {
      const data = await patch({
        payment_link: link.trim(),
      });

      onUpdate({
        ...conversion,
        ...data,
      });

      toast.success("Payment link saved");
    } catch (err) {
      console.error(err);
      toast.error("Could not save payment link");
    } finally {
      setSavingLink(false);
    }
  };

  const copyLivePageLink = async () => {
    try {
      const livePageUrl = `${window.location.origin}${conversion.funnel_path}`;

      await navigator.clipboard.writeText(livePageUrl);

      toast.success("Live TapToTable Page link copied");
    } catch (err) {
      console.error(err);
      toast.error("Copy failed");
    }
  };

  const markPaid = async () => {
    const savedPaymentLink = String(
      conversion.payment_link || ""
    ).trim();

    if (!savedPaymentLink) {
      toast.error("Save the payment link before marking this restaurant paid");
      return;
    }

    if (
      !window.confirm(
        "Mark this restaurant as paid? Its account will be activated."
      )
    ) {
      return;
    }

    setMarking(true);

    try {
      const data = await patch({
        payment_status: "paid",
      });

      onUpdate({
        ...conversion,
        ...data,
      });

      toast.success("Restaurant marked Paid · Account Active");
    } catch (err) {
      console.error(err);
      toast.error("Could not update payment status");
    } finally {
      setMarking(false);
    }
  };

  const isPaid = conversion.payment_status === "paid";

  return (
    <div
      className="mt-3 rounded-xl border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-4"
      data-testid={`conversion-panel-${conversion.lead_slug}`}
    >
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-zinc-400">Customer:</span>

        <span className="font-mono text-zinc-200">
          {conversion.email}
        </span>

        <span
          className={`rounded-full border px-2 py-0.5 ${badgeClass(
            conversion.payment_status
          )}`}
          data-testid={`conversion-payment-status-${conversion.lead_slug}`}
        >
          {conversion.payment_status}
        </span>

        <span
          className={`rounded-full border px-2 py-0.5 ${badgeClass(
            conversion.account_status
          )}`}
          data-testid={`conversion-account-status-${conversion.lead_slug}`}
        >
          {conversion.account_status}
        </span>

        <span className="ml-auto text-zinc-500">
          Live Page:{" "}
          <span className="font-mono text-zinc-300">
            {conversion.funnel_path}
          </span>
        </span>
      </div>

      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <input
          type="url"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="Paste Stripe payment link"
          className="flex-1 bg-[#1A1A1F] border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]"
          data-testid={`conversion-link-input-${conversion.lead_slug}`}
        />

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={saveLink}
            disabled={savingLink}
            className="text-xs bg-white/5 border border-white/10 rounded-full px-3 py-1.5 hover:bg-white/10 transition-colors disabled:opacity-50"
            data-testid={`conversion-save-link-${conversion.lead_slug}`}
          >
            {savingLink ? "Saving…" : "Save Payment Link"}
          </button>

          <button
            type="button"
            onClick={copyLivePageLink}
            className="text-xs bg-white/5 border border-white/10 rounded-full px-3 py-1.5 hover:bg-white/10 transition-colors"
            data-testid={`conversion-copy-link-${conversion.lead_slug}`}
          >
            Copy Live Page
          </button>

          {!isPaid && (
            <button
              type="button"
              onClick={markPaid}
              disabled={
                marking ||
                !String(conversion.payment_link || "").trim()
              }
              className="text-xs bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 rounded-full px-3 py-1.5 hover:bg-emerald-500/25 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              data-testid={`conversion-mark-paid-${conversion.lead_slug}`}
              title={
                !String(conversion.payment_link || "").trim()
                  ? "Save the payment link first"
                  : "Mark this restaurant paid and activate the account"
              }
            >
              {marking ? "…" : "Mark Restaurant Paid"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
